import { useEffect } from "react";
import { useRestaurant } from "@/hooks/use-restaurant";

/** Marks the runtime tenant favicon link so it can be updated without touching the static shell icon. */
export const TENANT_FAVICON_ATTR = "data-rx-tenant-favicon";

const TENANT_FAVICON_SELECTOR = `link[${TENANT_FAVICON_ATTR}="true"]`;

/**
 * Platform favicon path from `public/index.html`:
 * `<link rel="icon" href="%PUBLIC_URL%/favicon.ico" />`
 */
export function getPlatformFaviconHref(): string {
  const publicUrl = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  return `${publicUrl}/favicon.ico`;
}

function hasScheme(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);
}

/**
 * Conservative favicon URL gate.
 * Accepts absolute http(s) URLs and same-origin root-relative paths.
 * Returns the trimmed supplied value, or null.
 */
export function resolveTenantFaviconUrl(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/\s/.test(trimmed)) return null;
  if (trimmed.startsWith("//")) return null;

  if (hasScheme(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
      return trimmed;
    } catch {
      return null;
    }
  }

  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.includes("\\")) {
    return null;
  }
  if (hasScheme(trimmed.slice(1))) return null;

  try {
    // eslint-disable-next-line no-new
    new URL(trimmed, "https://validation.invalid");
    return trimmed;
  } catch {
    return null;
  }
}

function queryTenantFaviconLinks(): HTMLLinkElement[] {
  return Array.from(document.head.querySelectorAll(TENANT_FAVICON_SELECTOR)).filter(
    (el): el is HTMLLinkElement => el instanceof HTMLLinkElement
  );
}

function removeTenantFaviconLinks(): void {
  for (const link of queryTenantFaviconLinks()) {
    link.remove();
  }
}

function ensureTenantFaviconLink(href: string): HTMLLinkElement {
  const existing = queryTenantFaviconLinks();
  const link = existing[0] ?? document.createElement("link");
  for (const extra of existing.slice(1)) {
    extra.remove();
  }

  link.setAttribute("rel", "icon");
  link.setAttribute(TENANT_FAVICON_ATTR, "true");
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
  if (!link.isConnected) {
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Applies `useRestaurant().favicon_url` to a dedicated `<link rel="icon">`.
 * Invalid/missing/failed URLs leave only the static shell favicon (`favicon.ico`).
 */
export function useTenantFavicon(): void {
  const { favicon_url, restaurantId } = useRestaurant();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const href = resolveTenantFaviconUrl(favicon_url);
    if (!href) {
      removeTenantFaviconLinks();
      return;
    }

    let cancelled = false;
    const link = ensureTenantFaviconLink(href);

    const onError = (): void => {
      if (cancelled) return;
      removeTenantFaviconLinks();
    };

    link.addEventListener("error", onError);

    return () => {
      cancelled = true;
      link.removeEventListener("error", onError);
      removeTenantFaviconLinks();
    };
  }, [favicon_url, restaurantId]);
}

export function TenantFavicon(): null {
  useTenantFavicon();
  return null;
}
